import { useMutation } from "@apollo/client/react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { Link, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { z } from "zod"
import { gql } from "~/__generated__"
import { useViewerMaybe } from "~/auth/use-viewer"
import { cn } from "~/common/cn"
import { useDocumentTitle } from "~/common/use-document-title"
import { SiteHeader } from "~/layouts/site-header"
import { Button } from "~/ui/button"

const contactMutation = gql(/* GraphQL */ `
  mutation ContactMessageCreate($input: ContactMessageCreateInput!) {
    contactMessageCreate(input: $input) {
      success
    }
  }
`)

const formSchema = z.object({
  name: z.string().min(1, "Please tell us your name."),
  email: z.email({ message: "Please enter a valid email." }),
  subject: z.string().max(200).optional(),
  message: z
    .string()
    .min(1, "Please write a message.")
    .max(5000, "Message must be 5000 characters or fewer."),
})

type FormValues = z.infer<typeof formSchema>

const inputClassName =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"

const textareaClassName =
  "flex min-h-[160px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"

export const ContactScreen = () => {
  useDocumentTitle("Contact")
  const [searchParams] = useSearchParams()
  const wpId = searchParams.get("puzzleId") ?? undefined
  const { viewer } = useViewerMaybe()
  const [submitted, setSubmitted] = useState(false)

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: viewer?.firstName ? `${viewer.firstName} ${viewer.lastName ?? ""}`.trim() : "",
      email: viewer?.email ?? "",
      subject: wpId ? `Question about ${wpId}` : "",
      message: "",
    },
  })

  const [exec, { loading }] = useMutation(contactMutation)

  const onSubmit = async (values: FormValues) => {
    const result = await exec({
      variables: {
        input: {
          contactMessageInput: {
            name: values.name.trim(),
            email: values.email.trim(),
            subject: values.subject?.trim() || null,
            message: values.message.trim(),
            wpId: wpId ?? null,
          },
        },
      },
    })
    if (result.error) {
      toast.error("Could not send your message", {
        description: "Please check the form and try again.",
      })
      return
    }
    setSubmitted(true)
  }

  if (submitted) {
    return (
      <main className="mx-auto max-w-xl px-4 py-16 text-center">
        <h1 className="text-2xl font-semibold">Thank you for reaching out</h1>
        <p className="mt-2 text-gray-600">
          We received your message and sent a confirmation to your email.
        </p>
        <Link to="/" className="mt-8 inline-block text-blue-600 underline">
          Back to home
        </Link>
      </main>
    )
  }

  const errors = form.formState.errors

  return (
    <>
      <SiteHeader />
      <main className="mx-auto max-w-xl px-4 py-6 sm:py-12">
        <header>
          <h1 className="text-2xl font-semibold">Contact us</h1>
          <p className="mt-1 text-sm text-gray-600">
            Got a question, an idea, or a problem with a puzzle? Send us a note. We read every
            message.
          </p>
          {wpId ? (
            <p className="mt-3 rounded-md border bg-gray-50 p-3 text-sm">
              Reporting an issue with <span className="font-mono">{wpId}</span>.
            </p>
          ) : null}
        </header>

        <form className="mt-8 space-y-5" onSubmit={form.handleSubmit(onSubmit)}>
          <div>
            <label className="text-sm font-medium">
              Your name <span className="text-destructive">*</span>
            </label>
            <input {...form.register("name")} className={cn(inputClassName, "mt-1")} />
            {errors.name ? (
              <p className="mt-1 text-xs text-destructive">{errors.name.message}</p>
            ) : null}
          </div>

          <div>
            <label className="text-sm font-medium">
              Email <span className="text-destructive">*</span>
            </label>
            <input
              type="email"
              {...form.register("email")}
              className={cn(inputClassName, "mt-1")}
            />
            {errors.email ? (
              <p className="mt-1 text-xs text-destructive">{errors.email.message}</p>
            ) : null}
          </div>

          <div>
            <label className="text-sm font-medium">Subject</label>
            <input
              {...form.register("subject")}
              className={cn(inputClassName, "mt-1")}
              maxLength={200}
            />
          </div>

          <div>
            <label className="text-sm font-medium">
              Message <span className="text-destructive">*</span>
            </label>
            <textarea
              {...form.register("message")}
              className={cn(textareaClassName, "mt-1")}
              maxLength={5000}
            />
            {errors.message ? (
              <p className="mt-1 text-xs text-destructive">{errors.message.message}</p>
            ) : null}
          </div>

          <div className="flex items-center gap-4 border-t pt-6">
            <Button type="submit" disabled={loading}>
              {loading ? "Sending…" : "Send message"}
            </Button>
            <Link to="/" className="text-sm text-gray-500">
              Cancel
            </Link>
          </div>
        </form>
      </main>
    </>
  )
}
